import { computed, readonly, ref } from 'vue'

/** Per-device loudness, separate from mute so the speaker button remembers
 * the chosen level. Old mute settings still apply; new volume defaults to 50%. */
export function createAudioSettings(volumeKey: string, muteKey: string) {
  const volume = ref(0.5)
  const explicitlyMuted = ref(false)
  const muted = computed(() => explicitlyMuted.value || volume.value === 0)
  let loaded = false
  let lastAudible = 0.5

  function load() {
    if (loaded || typeof localStorage === 'undefined') return
    loaded = true
    try {
      const saved = localStorage.getItem(volumeKey)
      const value = saved === null || saved.trim() === '' ? NaN : Number(saved)
      if (Number.isFinite(value) && value >= 0 && value <= 1) volume.value = value
      explicitlyMuted.value = localStorage.getItem(muteKey) === '1'
      if (volume.value > 0) lastAudible = volume.value
    } catch { /* unavailable storage keeps this session usable */ }
  }

  function save() {
    try {
      localStorage.setItem(volumeKey, String(volume.value))
      localStorage.setItem(muteKey, explicitlyMuted.value ? '1' : '0')
    } catch { /* private mode / full storage */ }
  }

  return {
    volume: readonly(volume),
    muted,
    load,
    setVolume(value: number) {
      load()
      if (!Number.isFinite(value)) return
      volume.value = Math.max(0, Math.min(1, value))
      if (volume.value > 0) lastAudible = volume.value
      // Moving the slider is an explicit choice to hear this level.
      explicitlyMuted.value = false
      save()
    },
    toggleMute() {
      load()
      if (muted.value) {
        if (volume.value === 0) volume.value = lastAudible
        explicitlyMuted.value = false
      } else {
        explicitlyMuted.value = true
      }
      save()
    },
  }
}
